import { startTransition, useState } from "react";
import RightDrawer from "./Drawers/RightDrawer/RightDrawer";
import LeftDrawer from "./Drawers/LeftDrawer/LeftDrawer";
import TopBar from "./TopBar";
import styled from "styled-components/macro";
import ErrorBoundary from "../SimpleComponents/Errors/ErrorBoundary";
import {
  BREAKPOINT_DESKTOP_XL,
  NAV_HEIGHT_MOBILE_PX,
  NAV_HEIGHT_PX,
  Z_INDEX,
} from "../../constants/constants";
import FlightDebugger, {
  FlightDebuggerPopupDataContainer,
} from "./Drawers/RightDrawer/FlightDebugger/FlightDebugger";
import {
  useEventListener,
  useGetPageFromPath,
  useHideNav,
} from "utils/customHooks/genericHooks";
import { useAtom } from "jotai";
import { useMediaQuery } from "@mui/material";
import { isFlightDebuggerOpenAtom } from "./Drawers/RightDrawer/FlightDebugger/debuggerUtils";

const NavStyles = styled.div`
  width: 100vw;
  position: fixed;
  top: 0;
  z-index: ${Z_INDEX.L10};
  .startGuidedTourBeaconLocation {
    position: fixed;
    width: 0px;
    height: 0px;
    top: 28px;
    left: calc(75vw - 32px);
    @media (min-width: 600px) {
      top: 33px;
      left: calc(75vw - 75px);
    }
  }
  @media print {
    position: absolute;
    width: 100%;
  }
`;

const SpacerTop = styled.div`
  position: relative;
  width: 100%;
  /* if wifi banner is open, push content down to make room for the offline indicator banner */
  min-height: ${NAV_HEIGHT_MOBILE_PX}px;
  @media (min-width: 600px) {
    min-height: ${NAV_HEIGHT_PX}px;
  }
  @media (min-width: ${BREAKPOINT_DESKTOP_XL}px) {
    min-height: ${NAV_HEIGHT_PX * 1.5}px;
  }
  @media print {
    min-height: 0.5in !important;
  }
`;

const Nav = () => {
  const [isRightDrawerOpen, setIsRightDrawerOpen] = useState(false);
  const [isLeftDrawerOpen, setIsLeftDrawerOpen] = useState(false);
  useListenForFlightDebuggerOpen();

  const toggleRightDrawer = (rightDrawerOpen: boolean) => () => {
    setIsRightDrawerOpen(rightDrawerOpen);
  };
  const toggleLeftDrawer = (leftDrawerOpen: boolean) => () => {
    setIsLeftDrawerOpen(leftDrawerOpen);
  };

  const hideNav = useHideNav();
  const isPrint = useMediaQuery("print");
  const { isReportsPage } = useGetPageFromPath();
  const hideSpacer = isPrint && !isReportsPage;
  return hideNav ? null : (
    <>
      <NavStyles>
        <div className="startGuidedTourBeaconLocation" />
        <ErrorBoundary small={true} overlay={true} id="Nav_LeftDrawer">
          <LeftDrawer
            open={isLeftDrawerOpen}
            setIsLeftDrawerOpen={setIsLeftDrawerOpen}
            onOpen={toggleLeftDrawer(true)}
            onClose={toggleLeftDrawer(false)}
          />
        </ErrorBoundary>

        <ErrorBoundary small={true} overlay={true} id="Nav_TopBar">
          <TopBar
            onClickRightDrawerButton={toggleRightDrawer(true)}
            onClickLeftDrawerButton={toggleLeftDrawer(true)}
          />
        </ErrorBoundary>

        <ErrorBoundary small={true} overlay={true} id="Nav_RightDrawer">
          <RightDrawer
            open={isRightDrawerOpen}
            setIsRightDrawerOpen={setIsRightDrawerOpen}
            onOpen={toggleRightDrawer(true)}
            onClose={toggleRightDrawer(false)}
          />
        </ErrorBoundary>
      </NavStyles>
      {hideSpacer ? null : <SpacerTop />}
    </>
  );
};

function useListenForFlightDebuggerOpen() {
  const [, setIsFlightDebuggerOpen] = useAtom(isFlightDebuggerOpenAtom);
  // press Ctrl+Shift+Alt+Q to quickly toggle flight debugger
  useEventListener("keydown", e => {
    const isMac = navigator.platform.includes("Mac");
    const pcCombo =
      !isMac && e.ctrlKey && e.shiftKey && e.altKey && e.key === "Q";
    const macCombo = isMac && e.shiftKey && e.ctrlKey && e.key === "Q";
    if (pcCombo || macCombo) {
      startTransition(() => {
        setIsFlightDebuggerOpen(p => !p);
      });
    }
  });
}

export default Nav;

export function Debugger() {
  const [isDebuggerOpenAtom] = useAtom(isFlightDebuggerOpenAtom);
  const isDebuggerOpen =
    isDebuggerOpenAtom || window.location.search.includes("debugger=true");
  return (
    <>
      {isDebuggerOpen && (
        <ErrorBoundary small={true} overlay={true} id="Nav_FlightDebugger">
          <FlightDebugger />
          <FlightDebuggerPopupDataContainer />
        </ErrorBoundary>
      )}
    </>
  );
}
